import {
    Accordion,
    ActionIcon,
    Badge,
    Box,
    Button,
    Checkbox,
    Group,
    Loader,
    NumberInput,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { CityAttractionRO } from '@routellers/types/dist/city-attraction/city-attraction.ro';
import { AttractionReviewStatusEnum } from '@routellers/types/dist/city-attraction/enum/attraction-review-status.enum';
import { CityQuestRO } from '@routellers/types/dist/city-quest/city-quest.ro';
import {
    CityQuestCipherConfig,
    CityQuestPhotoVerification,
} from '@routellers/types/dist/city-quest/city-quest-content';
import {
    CityQuestArHuntMechanics,
    CityQuestArHuntTexts,
    CityQuestChaseMechanics,
    CityQuestChaseTexts,
    CityQuestChoiceText,
    CityQuestEvidenceTexts,
    CityQuestHintLevel,
    CityQuestHintText,
    CityQuestSensorMechanics,
    CityQuestSensorTexts,
    CityQuestTeamSplitRole,
} from '@routellers/types/dist/city-quest/city-quest-parts';
import { CityQuestRouteEventRO } from '@routellers/types/dist/city-quest/city-quest-route-event.ro';
import { CityQuestStopAudioPart } from '@routellers/types/dist/city-quest/generate-city-quest-audio.dto';
import { CityQuestStopRO } from '@routellers/types/dist/city-quest/city-quest-stop.ro';
import { CityQuestChallengeTypeEnum } from '@routellers/types/dist/city-quest/enum/city-quest-challenge-type.enum';
import { CityQuestRouteEventTypeEnum } from '@routellers/types/dist/city-quest/enum/city-quest-route-event-type.enum';
import { CountriesEnum } from '@routellers/types/dist/shared/enum/countries.enum';
import { Languages } from '@routellers/types/dist/shared/enum/languages.enum';
import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import { IconGripVertical, IconPlus, IconSearch, IconTrash, IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../../api';
import {
    EditorFooter,
    FieldHint,
    LanguageChips,
    MediaDropzone,
    Section,
} from '../../components/editor';
import { enumLabel, enumOptions } from '../../i18n';
import { contentLanguagesForCountry } from '../../utils/content-languages';
import { ConstructorMap } from '../route/constructor-map';

import {
    ArcLocaleDraft,
    FinaleArcPanel,
    PrologueArcPanel,
    csv as arcCsv,
    emptyArcLocale,
    finaleFilled,
    fromCsv as arcFromCsv,
    prologueFilled,
} from './quest-arc-panels';
import { BonusSecretDraft, StopExtrasAccordion } from './quest-stop-extras';
import {
    ArHuntConfigFields,
    ArHuntTextsFields,
    CipherConfigFields,
    DynamicAnswerFields,
    SensorConfigFields,
    SensorTextsFields,
} from './quest-stop-mechanics';

type Props = {
    questId: number;
    questCityId?: number | null;
    questCountry?: CountriesEnum | string | null;
    localePrimary?: Languages;
    onClose: () => void;
    onDirtyChange: (dirty: boolean) => void;
    onSaved?: () => void;
};

type StopLocaleDraft = {
    chapterTitle: string;
    displayNameBeforeSolve: string;
    storyBefore: string;
    storyAfter: string;
    historicalNote: string;
    arrivalInstruction: string;
    fromPrevious: string;
    walkMicroTask: string;
    challengePrompt: string;
    acceptedAnswers: string;
    choices: CityQuestChoiceText[];
    hints: CityQuestHintText[];
    teamSplitRoles: CityQuestTeamSplitRole[];
    solutionExplanation: string;
    photoChallengePrompt: string;
    fallbackPrompt: string;
    fallbackAcceptedAnswers: string;
    metaToken: string;
    sensorTexts: CityQuestSensorTexts | null;
    arHuntTexts: CityQuestArHuntTexts | null;
    cipher: CityQuestCipherConfig | null;
    evidence: CityQuestEvidenceTexts | null;
    bonusSecret: BonusSecretDraft | null;
    photoVerification: CityQuestPhotoVerification | null;
    thenPhotoCaption: string;
    storyAudioUrl: string;
    walkAudioUrl: string;
    challengeAudioUrl: string;
};

type EventLocaleDraft = {
    fromName: string;
    text: string;
    challengePrompt: string;
    acceptedAnswers: string;
    hints: CityQuestHintText[];
    solutionExplanation: string;
    chaseTexts: CityQuestChaseTexts | null;
    audioUrl: string;
};

/**
 * Точка в черновике. `id` есть только у сохранённых точек; `localKey` —
 * идентичность в UI, чтобы не терять выделение при переупорядочивании.
 */
type StopDraft = {
    localKey: string;
    id?: number;
    attractionId: number;
    gpsRadiusM: number;
    isKeyMilestone: boolean;
    photoUrl: string | null;
    challengeType: CityQuestChallengeTypeEnum;
    hintLevels: CityQuestHintLevel[];
    choiceIds: string[];
    dynamicAnswerRule: string | null;
    dynamicAnswerTolerance: string | null;
    photoCollageSlot: number | null;
    thenPhotoUrl: string | null;
    thenPhotoYear: string | null;
    videoUrl: string | null;
    videoPosterUrl: string | null;
    sensorConfig: CityQuestSensorMechanics | null;
    arHunt: CityQuestArHuntMechanics | null;
    evidenceImageUrl: string | null;
    bonusSecretPoints: number | null;
    expectedSolveSeconds: number | null;
    telemetryEventKey: string | null;
    locales: Partial<Record<Languages, StopLocaleDraft>>;
};

/**
 * События ссылаются на позицию точки, а не на её id: после переупорядочивания
 * и создания новых точек id известны только после сохранения.
 */
type EventDraft = {
    localKey: string;
    id?: number;
    key: string;
    type: CityQuestRouteEventTypeEnum;
    triggerAfterStopKey: string | null;
    triggerAfterStopsCompleted: number | null;
    bonusPoints: number | null;
    softTimerSeconds: number | null;
    challengeType: CityQuestChallengeTypeEnum | null;
    hintLevels: CityQuestHintLevel[];
    chase: CityQuestChaseMechanics | null;
    locales: Partial<Record<Languages, EventLocaleDraft>>;
};

const csv = (list: string[] | null | undefined) => (list || []).join(', ');
const fromCsv = (value: string) =>
    value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

const emptyStopLocale = (): StopLocaleDraft => ({
    chapterTitle: '',
    displayNameBeforeSolve: '',
    storyBefore: '',
    storyAfter: '',
    historicalNote: '',
    arrivalInstruction: '',
    fromPrevious: '',
    walkMicroTask: '',
    challengePrompt: '',
    acceptedAnswers: '',
    choices: [],
    hints: [],
    teamSplitRoles: [],
    solutionExplanation: '',
    photoChallengePrompt: '',
    fallbackPrompt: '',
    fallbackAcceptedAnswers: '',
    metaToken: '',
    sensorTexts: null,
    arHuntTexts: null,
    cipher: null,
    evidence: null,
    bonusSecret: null,
    photoVerification: null,
    thenPhotoCaption: '',
    storyAudioUrl: '',
    walkAudioUrl: '',
    challengeAudioUrl: '',
});

const emptyEventLocale = (): EventLocaleDraft => ({
    fromName: '',
    text: '',
    challengePrompt: '',
    acceptedAnswers: '',
    hints: [],
    solutionExplanation: '',
    chaseTexts: null,
    audioUrl: '',
});

const stopLocaleFrom = (stop: CityQuestStopRO, language: Languages): StopLocaleDraft => {
    const locale = stop.locales?.find((row) => row.language === language);

    return {
        chapterTitle: locale?.chapterTitle || '',
        displayNameBeforeSolve: locale?.displayNameBeforeSolve || '',
        storyBefore: locale?.storyBefore || '',
        storyAfter: locale?.storyAfter || '',
        historicalNote: locale?.historicalNote || '',
        arrivalInstruction: locale?.arrivalInstruction || '',
        fromPrevious: locale?.fromPrevious || '',
        walkMicroTask: locale?.walkMicroTask || '',
        challengePrompt: locale?.challengePrompt || '',
        acceptedAnswers: csv(locale?.acceptedAnswers),
        choices: locale?.choices || [],
        hints: locale?.hints || [],
        teamSplitRoles: locale?.teamSplitRoles || [],
        solutionExplanation: locale?.solutionExplanation || '',
        photoChallengePrompt: locale?.photoChallengePrompt || '',
        fallbackPrompt: locale?.fallbackPrompt || '',
        fallbackAcceptedAnswers: csv(locale?.fallbackAcceptedAnswers),
        metaToken: locale?.metaToken || '',
        sensorTexts: locale?.sensorTexts || null,
        arHuntTexts: locale?.arHuntTexts || null,
        cipher: locale?.cipher || null,
        evidence: locale?.evidence || null,
        // В черновике принимаемые ответы секрета — csv-строка, как у точки.
        bonusSecret: locale?.bonusSecret
            ? { ...locale.bonusSecret, acceptedAnswers: csv(locale.bonusSecret.acceptedAnswers) }
            : null,
        photoVerification: locale?.photoVerification || null,
        thenPhotoCaption: locale?.thenPhotoCaption || '',
        storyAudioUrl: locale?.storyAudioUrl || '',
        walkAudioUrl: locale?.walkAudioUrl || '',
        challengeAudioUrl: locale?.challengeAudioUrl || '',
    };
};

const eventLocaleFrom = (event: CityQuestRouteEventRO, language: Languages): EventLocaleDraft => {
    const locale = event.locales?.find((row) => row.language === language);

    return {
        fromName: locale?.fromName || '',
        text: locale?.text || '',
        challengePrompt: locale?.challengePrompt || '',
        acceptedAnswers: csv(locale?.acceptedAnswers),
        hints: locale?.hints || [],
        solutionExplanation: locale?.solutionExplanation || '',
        chaseTexts: locale?.chaseTexts || null,
        audioUrl: locale?.audioUrl || '',
    };
};

const stopLocalesFrom = (stop: CityQuestStopRO): Partial<Record<Languages, StopLocaleDraft>> => {
    const locales: Partial<Record<Languages, StopLocaleDraft>> = {};

    for (const row of stop.locales || []) {
        locales[row.language] = stopLocaleFrom(stop, row.language);
    }

    return locales;
};

const eventLocalesFrom = (
    event: CityQuestRouteEventRO,
): Partial<Record<Languages, EventLocaleDraft>> => {
    const locales: Partial<Record<Languages, EventLocaleDraft>> = {};

    for (const row of event.locales || []) {
        locales[row.language] = eventLocaleFrom(event, row.language);
    }

    return locales;
};

const stopPlayable = (draft?: StopLocaleDraft) =>
    Boolean(draft?.challengePrompt.trim() && fromCsv(draft.acceptedAnswers).length);

const normalizeSearch = (value: string) =>
    value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

const attractionSearchBlob = (attraction: CityAttractionRO) =>
    normalizeSearch(
        [
            attraction.title,
            attraction.displayTitle,
            attraction.knownFor,
            ...(attraction.locales || []).flatMap((locale) => [locale.title, locale.knownFor]),
        ]
            .filter(Boolean)
            .join('\n'),
    );

const stopDraftsFrom = (quest: CityQuestRO): StopDraft[] =>
    [...(quest.stops || [])]
        .sort((a, b) => a.order - b.order)
        .map((stop) => ({
            localKey: `stop-${stop.id}`,
            id: stop.id,
            attractionId: stop.attractionId,
            gpsRadiusM: stop.gpsRadiusM,
            isKeyMilestone: stop.isKeyMilestone,
            photoUrl: stop.photoUrl,
            challengeType: stop.challengeType,
            hintLevels: stop.hintLevels || [],
            choiceIds: stop.choiceIds || [],
            dynamicAnswerRule: stop.dynamicAnswerRule,
            dynamicAnswerTolerance: stop.dynamicAnswerTolerance,
            photoCollageSlot: stop.photoCollageSlot,
            thenPhotoUrl: stop.thenPhotoUrl,
            thenPhotoYear: stop.thenPhotoYear,
            videoUrl: stop.videoUrl,
            videoPosterUrl: stop.videoPosterUrl,
            sensorConfig: stop.sensorConfig,
            arHunt: stop.arHunt,
            evidenceImageUrl: stop.evidenceImageUrl,
            bonusSecretPoints: stop.bonusSecretPoints,
            expectedSolveSeconds: stop.expectedSolveSeconds,
            telemetryEventKey: stop.telemetryEventKey,
            locales: stopLocalesFrom(stop),
        }));

const eventDraftsFrom = (quest: CityQuestRO): EventDraft[] =>
    (quest.routeEvents || []).map((event) => ({
        localKey: `event-${event.id}`,
        id: event.id,
        key: event.key,
        type: event.type,
        triggerAfterStopKey:
            event.triggerAfterStopId != null ? `stop-${event.triggerAfterStopId}` : null,
        triggerAfterStopsCompleted: event.triggerAfterStopsCompleted,
        bonusPoints: event.bonusPoints,
        softTimerSeconds: event.softTimerSeconds,
        challengeType: event.challengeType,
        hintLevels: event.hintLevels || [],
        chase: event.chase,
        locales: eventLocalesFrom(event),
    }));

/** Сенсор и AR-охота закрываются служебным ответом `done` — гарантируем его. */
const ensureDoneAnswer = (answers: string[]) =>
    answers.some((answer) => answer.toLowerCase() === 'done') ? answers : [...answers, 'done'];

const needsDoneAnswer = (challengeType: CityQuestChallengeTypeEnum) =>
    challengeType === CityQuestChallengeTypeEnum.SENSOR ||
    challengeType === CityQuestChallengeTypeEnum.AR_HUNT;

/** Пустые json-объекты не сохраняем — null, как «поля нет». */
const sensorTextsPayload = (texts: CityQuestSensorTexts | null) =>
    texts && (texts.actionLabel?.trim() || texts.successText?.trim()) ? texts : null;

const arHuntTextsPayload = (texts: CityQuestArHuntTexts | null) =>
    texts &&
    (texts.successText?.trim() ||
        Object.values(texts.targetHints || {}).some((hint) => hint.trim()))
        ? texts
        : null;

const evidencePayload = (evidence: CityQuestEvidenceTexts | null) =>
    evidence && (evidence.id.trim() || evidence.title.trim() || evidence.description?.trim())
        ? evidence
        : null;

const bonusSecretPayload = (draft: BonusSecretDraft | null) => {
    if (!draft) return null;

    const acceptedAnswers = fromCsv(draft.acceptedAnswers);

    if (!draft.prompt.trim() && !acceptedAnswers.length && !draft.teaser?.trim()) return null;

    return { ...draft, acceptedAnswers };
};

const photoVerificationPayload = (verification: CityQuestPhotoVerification | null) =>
    verification && verification.expected.trim() ? verification : null;

const chaseTextsPayload = (texts: CityQuestChaseTexts | null) =>
    texts && (texts.targetLabel?.trim() || texts.successText?.trim() || texts.failText?.trim())
        ? texts
        : null;

const arHuntPayload = (arHunt: CityQuestArHuntMechanics | null) =>
    arHunt && (arHunt.symbols.length || arHunt.collectRadiusM != null) ? arHunt : null;

const localePayloadFromStop = (stop: StopDraft) =>
    (Object.entries(stop.locales) as [Languages, StopLocaleDraft][]).map(([language, draft]) => ({
        language,
        chapterTitle: draft.chapterTitle.trim() || null,
        displayNameBeforeSolve: draft.displayNameBeforeSolve.trim() || null,
        storyBefore: draft.storyBefore.trim() || null,
        storyAfter: draft.storyAfter.trim() || null,
        historicalNote: draft.historicalNote.trim() || null,
        arrivalInstruction: draft.arrivalInstruction.trim() || null,
        fromPrevious: draft.fromPrevious.trim() || null,
        walkMicroTask: draft.walkMicroTask.trim() || null,
        challengePrompt: draft.challengePrompt.trim() || null,
        acceptedAnswers: needsDoneAnswer(stop.challengeType)
            ? ensureDoneAnswer(fromCsv(draft.acceptedAnswers))
            : fromCsv(draft.acceptedAnswers),
        choices: draft.choices,
        hints: draft.hints,
        teamSplitRoles: draft.teamSplitRoles,
        solutionExplanation: draft.solutionExplanation.trim() || null,
        photoChallengePrompt: draft.photoChallengePrompt.trim() || null,
        fallbackPrompt: draft.fallbackPrompt.trim() || null,
        fallbackAcceptedAnswers: fromCsv(draft.fallbackAcceptedAnswers),
        metaToken: draft.metaToken.trim() || null,
        sensorTexts: sensorTextsPayload(draft.sensorTexts),
        arHuntTexts: arHuntTextsPayload(draft.arHuntTexts),
        cipher: draft.cipher,
        evidence: evidencePayload(draft.evidence),
        bonusSecret: bonusSecretPayload(draft.bonusSecret),
        photoVerification: photoVerificationPayload(draft.photoVerification),
        thenPhotoCaption: draft.thenPhotoCaption.trim() || null,
        storyAudioUrl: draft.storyAudioUrl.trim() || null,
        walkAudioUrl: draft.walkAudioUrl.trim() || null,
        challengeAudioUrl: draft.challengeAudioUrl.trim() || null,
    }));

const localePayloadFromEvent = (locales: Partial<Record<Languages, EventLocaleDraft>>) =>
    (Object.entries(locales) as [Languages, EventLocaleDraft][]).map(([language, draft]) => ({
        language,
        fromName: draft.fromName.trim() || null,
        text: draft.text.trim() || null,
        challengePrompt: draft.challengePrompt.trim() || null,
        acceptedAnswers: fromCsv(draft.acceptedAnswers),
        hints: draft.hints,
        solutionExplanation: draft.solutionExplanation.trim() || null,
        chaseTexts: chaseTextsPayload(draft.chaseTexts),
        audioUrl: draft.audioUrl.trim() || null,
    }));

export const QuestConstructorTab = ({
    questId,
    questCityId,
    questCountry,
    localePrimary,
    onClose,
    onDirtyChange,
    onSaved,
}: Props) => {
    const { t } = useTranslation();
    const challengeTypeOptions = useMemo(
        () => enumOptions(Object.values(CityQuestChallengeTypeEnum), t, 'enums.challengeType'),
        [t],
    );
    const eventTypeOptions = useMemo(
        () => enumOptions(Object.values(CityQuestRouteEventTypeEnum), t, 'enums.routeEventType'),
        [t],
    );
    const contentLanguages = useMemo(
        () => contentLanguagesForCountry(questCountry),
        [questCountry],
    );
    const [contentLang, setContentLang] = useState<Languages>(
        () =>
            (localePrimary && contentLanguages.includes(localePrimary)
                ? localePrimary
                : contentLanguages[0]) || Languages.EN,
    );
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [stops, setStops] = useState<StopDraft[]>([]);
    const [events, setEvents] = useState<EventDraft[]>([]);
    const [baseline, setBaseline] = useState('');
    const [attractionsById, setAttractionsById] = useState<Record<string, CityAttractionRO>>({});
    const [cityAttractions, setCityAttractions] = useState<CityAttractionRO[]>([]);
    const [selectedStopKey, setSelectedStopKey] = useState<string | null>(null);
    const [selectedAttractionId, setSelectedAttractionId] = useState<number | null>(null);
    const [selectedBookend, setSelectedBookend] = useState<'prologue' | 'finale' | null>(null);
    const [arcLocales, setArcLocales] = useState<Partial<Record<Languages, ArcLocaleDraft>>>({});
    const [mapSearch, setMapSearch] = useState('');
    const [draggingKey, setDraggingKey] = useState<string | null>(null);
    const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);
    const [ttsPart, setTtsPart] = useState<CityQuestStopAudioPart | null>(null);

    const snapshot = useMemo(
        () => JSON.stringify({ stops, events, arcLocales }),
        [stops, events, arcLocales],
    );
    const dirty = baseline !== '' && snapshot !== baseline;

    useEffect(() => {
        onDirtyChange(dirty);
    }, [dirty, onDirtyChange]);

    useEffect(() => {
        if (!contentLanguages.includes(contentLang)) {
            setContentLang(contentLanguages[0] || Languages.EN);
        }
    }, [contentLanguages, contentLang]);

    const applyQuest = useCallback(
        (quest: CityQuestRO) => {
            const nextStops = stopDraftsFrom(quest);
            const nextEvents = eventDraftsFrom(quest);
            const nextArc: Partial<Record<Languages, ArcLocaleDraft>> = {};

            for (const lang of contentLanguages) {
                const locale = quest.locales?.find((row) => row.language === lang);

                nextArc[lang] = locale
                    ? {
                          prologue: locale.prologue || [],
                          prologueAudioUrl: locale.prologueAudioUrl || '',
                          finaleChoicePrompt: locale.finaleChoicePrompt || '',
                          finaleChoices: locale.finaleChoices || [],
                          finalPuzzlePrompt: locale.finalPuzzlePrompt || '',
                          finalPuzzleAcceptedAnswers: arcCsv(locale.finalPuzzleAcceptedAnswers),
                          finalPuzzleHints: locale.finalPuzzleHints || [],
                          finalPuzzleSolutionExplanation:
                              locale.finalPuzzleSolutionExplanation || '',
                          finaleAudioUrl: locale.finaleAudioUrl || '',
                      }
                    : emptyArcLocale();
            }

            setStops(nextStops);
            setEvents(nextEvents);
            setArcLocales(nextArc);
            setBaseline(
                JSON.stringify({ stops: nextStops, events: nextEvents, arcLocales: nextArc }),
            );

            return nextStops;
        },
        [contentLanguages],
    );

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);

            try {
                const { data } = await api.cityQuestsApi.getContent(questId);

                if (cancelled || !data?.quest) return;

                applyQuest(data.quest);

                setAttractionsById(data.attractionsById || {});
                setSelectedBookend('prologue');
                setSelectedStopKey(null);
                setSelectedAttractionId(null);

                if (questCityId) {
                    const attractions = await api.cityAttractionsApi.query({
                        page: 1,
                        limit: 100,
                        where: { cityId: questCityId } as Record<string, unknown>,
                    });

                    if (!cancelled) setCityAttractions(attractions.data?.items || []);
                }
            } catch (error) {
                notifications.show({ color: 'red', message: apiErrorMessage(error) });
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [questId, questCityId, applyQuest]);

    const cityAttractionsById = useMemo(() => {
        const map: Record<string, CityAttractionRO> = {};

        for (const a of cityAttractions) map[String(a.id)] = a;

        return map;
    }, [cityAttractions]);

    const getAttraction = useCallback(
        (id?: number | null) =>
            id == null ? undefined : attractionsById[String(id)] || cityAttractionsById[String(id)],
        [attractionsById, cityAttractionsById],
    );

    const selectedStop = stops.find((s) => s.localKey === selectedStopKey) || null;
    const selectedAttraction =
        selectedAttractionId == null ? null : getAttraction(selectedAttractionId) || null;
    const selectedStopLocale = selectedStop?.locales[contentLang] || emptyStopLocale();

    // Options must include stop-bound attractions from the quest payload even
    // when they are outside the city query page (limit/archive/filter).
    // Otherwise Mantine Select keeps the value but shows an empty input.
    const attractionOptions = useMemo(() => {
        const byId = new Map<string, { value: string; label: string }>();

        const add = (attraction: CityAttractionRO) => {
            byId.set(String(attraction.id), {
                value: String(attraction.id),
                label: attraction.displayTitle || attraction.title || `#${attraction.id}`,
            });
        };

        for (const attraction of cityAttractions) add(attraction);
        for (const attraction of Object.values(attractionsById)) add(attraction);

        return [...byId.values()];
    }, [cityAttractions, attractionsById]);

    const mapPool = useMemo(
        () =>
            cityAttractions.filter(
                (a) =>
                    !a.archived &&
                    a.reviewStatus !== AttractionReviewStatusEnum.REJECTED &&
                    a.reviewStatus !== AttractionReviewStatusEnum.REJECTED_MCP,
            ),
        [cityAttractions],
    );

    const center = useMemo(() => {
        const withCoords = cityAttractions.find((a) => a.lat != null && a.lng != null);

        return withCoords
            ? { lat: withCoords.lat as number, lng: withCoords.lng as number }
            : { lat: 52.52, lng: 13.405 };
    }, [cityAttractions]);

    const stopTitle = (stop: StopDraft) => {
        const attraction = getAttraction(stop.attractionId);
        const chapter = stop.locales[contentLang]?.chapterTitle?.trim();

        return (
            chapter ||
            attraction?.displayTitle ||
            attraction?.title ||
            t('editors.quest.constructor.stopFallback', { id: stop.attractionId })
        );
    };

    const mapStops = useMemo(
        () =>
            stops
                .map((stop, index) => {
                    const attraction = getAttraction(stop.attractionId);

                    if (!attraction || attraction.lat == null || attraction.lng == null)
                        return null;

                    return {
                        key: stop.localKey,
                        order: index,
                        kind: 'attraction' as const,
                        attractionId: stop.attractionId,
                        lat: attraction.lat,
                        lng: attraction.lng,
                        title: attraction.displayTitle || attraction.title,
                    };
                })
                .filter((s): s is NonNullable<typeof s> => !!s),
        [stops, getAttraction],
    );

    const mapSearchHits = useMemo(() => {
        const query = normalizeSearch(mapSearch);

        if (!query) {
            return { active: false, attractionIds: [] as number[], stopKeys: [] as string[] };
        }

        const attractionIds = mapPool
            .filter((attraction) => attractionSearchBlob(attraction).includes(query))
            .map((attraction) => attraction.id);
        const hitIds = new Set(attractionIds);

        return {
            active: true,
            attractionIds,
            stopKeys: stops.filter((s) => hitIds.has(s.attractionId)).map((s) => s.localKey),
        };
    }, [mapSearch, mapPool, stops]);

    const activeArcLocale = arcLocales[contentLang] || emptyArcLocale();

    const contentLangChips = contentLanguages.map((lang) => {
        let mark: 'empty' | 'filled' | 'dirty' = 'empty';

        if (selectedBookend === 'prologue') {
            if (lang === contentLang && dirty) mark = 'dirty';
            else if (prologueFilled(arcLocales[lang])) mark = 'filled';
        } else if (selectedBookend === 'finale') {
            if (lang === contentLang && dirty) mark = 'dirty';
            else if (finaleFilled(arcLocales[lang])) mark = 'filled';
        } else {
            const locale = selectedStop?.locales[lang];

            if (lang === contentLang && dirty && selectedStop) mark = 'dirty';
            else if (stopPlayable(locale)) mark = 'filled';
        }

        return { value: lang, label: lang.toUpperCase(), mark };
    });

    const patchStop = (localKey: string, patch: Partial<StopDraft>) => {
        setStops((list) =>
            list.map((stop) => (stop.localKey === localKey ? { ...stop, ...patch } : stop)),
        );
    };

    const patchStopLocale = (localKey: string, patch: Partial<StopLocaleDraft>) => {
        setStops((list) =>
            list.map((stop) => {
                if (stop.localKey !== localKey) return stop;

                const current = stop.locales[contentLang] || emptyStopLocale();

                return {
                    ...stop,
                    locales: {
                        ...stop.locales,
                        [contentLang]: { ...current, ...patch },
                    },
                };
            }),
        );
    };

    const patchEventLocale = (localKey: string, patch: Partial<EventLocaleDraft>) => {
        setEvents((list) =>
            list.map((event) => {
                if (event.localKey !== localKey) return event;

                const current = event.locales[contentLang] || emptyEventLocale();

                return {
                    ...event,
                    locales: {
                        ...event.locales,
                        [contentLang]: { ...current, ...patch },
                    },
                };
            }),
        );
    };

    const selectStop = (localKey: string) => {
        setSelectedStopKey(localKey);
        setSelectedAttractionId(null);
        setSelectedBookend(null);
    };

    const selectBookend = (bookend: 'prologue' | 'finale') => {
        setSelectedBookend(bookend);
        setSelectedStopKey(null);
        setSelectedAttractionId(null);
    };

    const patchArcLocale = (patch: Partial<ArcLocaleDraft>) => {
        setArcLocales((prev) => ({
            ...prev,
            [contentLang]: { ...(prev[contentLang] || emptyArcLocale()), ...patch },
        }));
    };

    /**
     * TTS для сохранённого стопа. Текст берём с экрана, чтобы озвучка не
     * отставала от несохранённых правок; URL сервер пишет в локаль сам.
     */
    const generateStopTts = async (part: CityQuestStopAudioPart) => {
        if (!selectedStop?.id) return;

        const sourceByPart = {
            story: selectedStopLocale.storyBefore,
            walk: selectedStopLocale.walkMicroTask,
            challenge: selectedStopLocale.challengePrompt,
        } as const;

        setTtsPart(part);

        try {
            const { data } = await api.cityQuestsApi.generateStopAudio({
                stopId: selectedStop.id,
                data: {
                    language: contentLang,
                    part,
                    text: sourceByPart[part].trim() || undefined,
                },
            });

            if (data?.url) {
                patchStopLocale(selectedStop.localKey, {
                    [`${part}AudioUrl`]: data.url,
                } as Partial<StopLocaleDraft>);
            }

            notifications.show({
                color: 'green',
                message: t('editors.quest.constructor.stopTexts.audioGenerated'),
            });
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setTtsPart(null);
        }
    };

    const addStopForAttraction = (attraction: CityAttractionRO) => {
        const existing = stops.find((s) => s.attractionId === attraction.id);

        if (existing) {
            selectStop(existing.localKey);

            return;
        }

        const draft: StopDraft = {
            localKey: `new-${Date.now()}`,
            attractionId: attraction.id,
            gpsRadiusM: 40,
            isKeyMilestone: false,
            photoUrl: null,
            challengeType: CityQuestChallengeTypeEnum.RIDDLE,
            hintLevels: [],
            choiceIds: [],
            dynamicAnswerRule: null,
            dynamicAnswerTolerance: null,
            photoCollageSlot: null,
            thenPhotoUrl: null,
            thenPhotoYear: null,
            videoUrl: null,
            videoPosterUrl: null,
            sensorConfig: null,
            arHunt: null,
            evidenceImageUrl: null,
            bonusSecretPoints: null,
            expectedSolveSeconds: null,
            telemetryEventKey: null,
            locales: {},
        };

        setStops((list) => [...list, draft]);
        selectStop(draft.localKey);
    };

    const removeStop = (localKey: string) => {
        const stop = stops.find((s) => s.localKey === localKey);
        const dependent = events.filter((e) => e.triggerAfterStopKey === localKey);

        if (
            stop?.id &&
            !window.confirm(
                t('editors.quest.constructor.removeStopConfirm', { title: stopTitle(stop) }) +
                    (dependent.length
                        ? t('editors.quest.constructor.removeStopEventsWarning', {
                              count: dependent.length,
                          })
                        : ''),
            )
        ) {
            return;
        }

        setStops((list) => list.filter((s) => s.localKey !== localKey));
        setEvents((list) =>
            list.map((e) =>
                e.triggerAfterStopKey === localKey ? { ...e, triggerAfterStopKey: null } : e,
            ),
        );
        if (selectedStopKey === localKey) setSelectedStopKey(null);
    };

    const reorderStop = (fromKey: string, toKey: string) => {
        if (fromKey === toKey) return;

        setStops((list) => {
            const from = list.findIndex((s) => s.localKey === fromKey);
            const to = list.findIndex((s) => s.localKey === toKey);

            if (from < 0 || to < 0) return list;

            const next = [...list];
            const [item] = next.splice(from, 1);

            next.splice(to, 0, item);

            return next;
        });
    };

    const addEvent = () => {
        setEvents((list) => [
            ...list,
            {
                localKey: `new-event-${Date.now()}`,
                key: `re_${list.length + 1}`,
                type: CityQuestRouteEventTypeEnum.MESSAGE,
                triggerAfterStopKey: stops[0]?.localKey || null,
                triggerAfterStopsCompleted: null,
                bonusPoints: null,
                softTimerSeconds: null,
                challengeType: null,
                hintLevels: [],
                chase: null,
                locales: {},
            },
        ]);
    };

    const patchEvent = (localKey: string, patch: Partial<EventDraft>) => {
        setEvents((list) =>
            list.map((event) => (event.localKey === localKey ? { ...event, ...patch } : event)),
        );
    };

    const save = async () => {
        if (!stops.length) {
            notifications.show({
                color: 'red',
                message: t('editors.quest.constructor.atLeastOneStop'),
            });

            return;
        }

        // Триггер — конкретная точка или счётчик закрытых точек (free-order).
        const unlinked = events.filter(
            (e) => !e.triggerAfterStopKey && e.triggerAfterStopsCompleted == null,
        );

        if (unlinked.length) {
            notifications.show({
                color: 'red',
                message: t('editors.quest.constructor.eventNoTrigger', {
                    key: unlinked[0].key,
                }),
            });

            return;
        }

        setSaving(true);

        try {
            const { data: withStops } = await api.cityQuestsApi.upsertStops({
                id: questId,
                data: {
                    stops: stops.map((stop, index) => ({
                        id: stop.id,
                        order: index + 1,
                        attractionId: stop.attractionId,
                        gpsRadiusM: stop.gpsRadiusM,
                        isKeyMilestone: stop.isKeyMilestone,
                        photoUrl: stop.photoUrl,
                        challengeType: stop.challengeType,
                        hintLevels: stop.hintLevels,
                        choiceIds: stop.choiceIds,
                        dynamicAnswerRule: stop.dynamicAnswerRule,
                        dynamicAnswerTolerance: stop.dynamicAnswerTolerance,
                        photoCollageSlot: stop.photoCollageSlot,
                        thenPhotoUrl: stop.thenPhotoUrl,
                        thenPhotoYear: stop.thenPhotoYear,
                        videoUrl: stop.videoUrl,
                        videoPosterUrl: stop.videoPosterUrl,
                        sensorConfig: stop.sensorConfig,
                        arHunt: arHuntPayload(stop.arHunt),
                        evidenceImageUrl: stop.evidenceImageUrl,
                        bonusSecretPoints: stop.bonusSecretPoints,
                        expectedSolveSeconds: stop.expectedSolveSeconds,
                        telemetryEventKey: stop.telemetryEventKey,
                        locales: localePayloadFromStop(stop),
                    })),
                },
            });

            // Новые точки получили id только сейчас — события связываем по позиции.
            const savedStops = [...(withStops?.stops || [])].sort((a, b) => a.order - b.order);
            const idByLocalKey = new Map<string, number>();

            stops.forEach((stop, index) => {
                const saved = savedStops[index];

                if (saved) idByLocalKey.set(stop.localKey, saved.id);
            });

            const { data: withEvents } = await api.cityQuestsApi.upsertRouteEvents({
                id: questId,
                data: {
                    routeEvents: events.map((event) => ({
                        id: event.id,
                        key: event.key.trim(),
                        type: event.type,
                        triggerAfterStopId: event.triggerAfterStopKey
                            ? (idByLocalKey.get(event.triggerAfterStopKey) ?? null)
                            : null,
                        triggerAfterStopsCompleted: event.triggerAfterStopsCompleted,
                        bonusPoints: event.bonusPoints,
                        softTimerSeconds: event.softTimerSeconds,
                        challengeType: event.challengeType,
                        hintLevels: event.hintLevels,
                        chase: event.chase,
                        locales: localePayloadFromEvent(event.locales),
                    })),
                },
            });

            for (const lang of contentLanguages) {
                const draft = arcLocales[lang] || emptyArcLocale();

                await api.cityQuestsApi.upsertLocale({
                    id: questId,
                    data: {
                        language: lang,
                        prologue: draft.prologue,
                        prologueAudioUrl: draft.prologueAudioUrl.trim() || null,
                        finaleChoicePrompt: draft.finaleChoicePrompt.trim() || null,
                        finaleChoices: draft.finaleChoices,
                        finalPuzzlePrompt: draft.finalPuzzlePrompt.trim() || null,
                        finalPuzzleAcceptedAnswers: arcFromCsv(draft.finalPuzzleAcceptedAnswers),
                        finalPuzzleHints: draft.finalPuzzleHints,
                        finalPuzzleSolutionExplanation:
                            draft.finalPuzzleSolutionExplanation.trim() || null,
                        finaleAudioUrl: draft.finaleAudioUrl.trim() || null,
                    },
                });
            }

            const { data: refreshed } = await api.cityQuestsApi.getOne(questId);
            const fresh = refreshed || withEvents || withStops;

            if (fresh) {
                const nextStops = applyQuest(fresh);
                const bookend = selectedBookend;

                if (bookend) {
                    setSelectedBookend(bookend);
                    setSelectedStopKey(null);
                } else {
                    setSelectedStopKey((current) => {
                        if (!current) return nextStops[0]?.localKey || null;
                        const id = idByLocalKey.get(current);

                        return id ? `stop-${id}` : nextStops[0]?.localKey || null;
                    });
                }
            }

            notifications.show({
                color: 'green',
                message: t('editors.quest.constructor.stopsSaved'),
            });
            onSaved?.();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Loader />;

    const attraction = selectedStop ? getAttraction(selectedStop.attractionId) : null;

    return (
        <Stack gap='md' h='100%'>
            <Box
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(300px, 1.2fr) 300px minmax(340px, 1.3fr)',
                    gap: 16,
                    minHeight: 520,
                    alignItems: 'stretch',
                }}
            >
                <Stack gap='sm' style={{ minWidth: 0 }}>
                    <Group justify='space-between' wrap='nowrap' gap='sm'>
                        <TextInput
                            size='xs'
                            placeholder={t('editors.quest.constructor.searchPlaceholder')}
                            value={mapSearch}
                            onChange={(e) => setMapSearch(e.currentTarget.value)}
                            leftSection={<IconSearch size={14} />}
                            rightSection={
                                mapSearch ? (
                                    <ActionIcon
                                        size='sm'
                                        variant='subtle'
                                        aria-label={t('common.clearSearch')}
                                        onClick={() => setMapSearch('')}
                                    >
                                        <IconX size={14} />
                                    </ActionIcon>
                                ) : null
                            }
                            style={{ flex: 1, maxWidth: 320 }}
                        />
                        <Text size='xs' c='dimmed' style={{ flexShrink: 0 }}>
                            {t('editors.quest.constructor.placesStopsCount', {
                                places: mapPool.length,
                                stops: stops.length,
                            })}
                        </Text>
                    </Group>
                    <ConstructorMap
                        center={center}
                        attractions={mapPool.map((a) => ({
                            placeKey: `attraction:${a.id}`,
                            title: a.displayTitle || a.title,
                            lat: a.lat,
                            lng: a.lng,
                            reviewStatus: a.reviewStatus,
                            archived: a.archived,
                        }))}
                        stops={mapStops.map((s) => ({
                            ...s,
                            placeKey: `attraction:${s.attractionId}`,
                        }))}
                        geometry={[]}
                        selectedStopKey={selectedStopKey}
                        selectedPlaceKey={
                            selectedAttractionId == null
                                ? null
                                : `attraction:${selectedAttractionId}`
                        }
                        searchActive={mapSearchHits.active}
                        highlightedPlaceKeys={mapSearchHits.attractionIds.map(
                            (id) => `attraction:${id}`,
                        )}
                        highlightedStopKeys={mapSearchHits.stopKeys}
                        onSelectPlace={(key) => {
                            setSelectedAttractionId(Number(key.split(':')[1]));
                            setSelectedStopKey(null);
                            setSelectedBookend(null);
                        }}
                        onSelectStop={selectStop}
                        onAddWaypoint={() => undefined}
                        readOnly
                        onClearSelection={() => { setSelectedAttractionId(null); setSelectedStopKey(null); setSelectedBookend(null); }}
                        onMovePoint={async () => undefined}
                    />
                </Stack>

                <Box style={{ width: 300, maxHeight: '70vh', overflow: 'auto', minWidth: 0 }}>
                    <Section
                        title={t('editors.quest.constructor.stopsTitle')}
                        description={t('editors.quest.constructor.stopsDescription')}
                    >
                        <Stack gap={6}>
                            <Box
                                onClick={() => selectBookend('prologue')}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '8px 10px',
                                    border:
                                        selectedBookend === 'prologue'
                                            ? '1px solid var(--mantine-color-blue-5)'
                                            : '1px solid var(--mantine-color-gray-3)',
                                    borderRadius: 'var(--mantine-radius-sm)',
                                    background:
                                        selectedBookend === 'prologue'
                                            ? 'var(--mantine-color-blue-0)'
                                            : 'var(--mantine-color-gray-0)',
                                    cursor: 'pointer',
                                }}
                            >
                                <Box style={{ flex: '1 1 auto', minWidth: 0 }}>
                                    <Text size='sm' fw={600} truncate>
                                        {t('editors.quest.constructor.bookendPrologue')}
                                    </Text>
                                    <Text size='xs' c='dimmed' truncate>
                                        {t('editors.quest.constructor.bookendFixed')}
                                        {prologueFilled(arcLocales[contentLang])
                                            ? ''
                                            : ` · ${t('editors.quest.constructor.noText')}`}
                                    </Text>
                                </Box>
                            </Box>

                            {stops.map((stop, index) => {
                                const active = stop.localKey === selectedStopKey;

                                return (
                                    <Box
                                        key={stop.localKey}
                                        draggable
                                        onDragStart={(e) => {
                                            e.dataTransfer.effectAllowed = 'move';
                                            e.dataTransfer.setData('text/plain', stop.localKey);
                                            setDraggingKey(stop.localKey);
                                        }}
                                        onDragEnd={() => {
                                            setDraggingKey(null);
                                            setDropTargetKey(null);
                                        }}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.dataTransfer.dropEffect = 'move';
                                            if (dropTargetKey !== stop.localKey) {
                                                setDropTargetKey(stop.localKey);
                                            }
                                        }}
                                        onDragLeave={() => {
                                            if (dropTargetKey === stop.localKey) {
                                                setDropTargetKey(null);
                                            }
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            const fromKey =
                                                e.dataTransfer.getData('text/plain') || draggingKey;

                                            if (fromKey) reorderStop(fromKey, stop.localKey);
                                            setDraggingKey(null);
                                            setDropTargetKey(null);
                                        }}
                                        onClick={() => selectStop(stop.localKey)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '8px 10px',
                                            border:
                                                dropTargetKey === stop.localKey
                                                    ? '1px dashed var(--mantine-color-blue-5)'
                                                    : active
                                                      ? '1px solid var(--mantine-color-blue-5)'
                                                      : '1px solid var(--mantine-color-gray-3)',
                                            borderRadius: 'var(--mantine-radius-sm)',
                                            background: active
                                                ? 'var(--mantine-color-blue-0)'
                                                : 'var(--mantine-color-body)',
                                            opacity: draggingKey === stop.localKey ? 0.45 : 1,
                                            cursor: 'grab',
                                        }}
                                    >
                                        <IconGripVertical
                                            size={16}
                                            color='var(--mantine-color-gray-5)'
                                            style={{ flex: '0 0 auto' }}
                                        />
                                        <Box
                                            style={{
                                                flex: '1 1 auto',
                                                minWidth: 0,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <Text size='sm' fw={500} truncate>
                                                {index + 1}. {stopTitle(stop)}
                                            </Text>
                                            <Text size='xs' c='dimmed' truncate>
                                                {enumLabel(
                                                    t,
                                                    'enums.challengeType',
                                                    stop.challengeType,
                                                )}
                                                {stop.isKeyMilestone
                                                    ? ` · ${t('editors.quest.constructor.milestone')}`
                                                    : ''}
                                                {stopPlayable(stop.locales[contentLang])
                                                    ? ''
                                                    : ` · ${t('editors.quest.constructor.noText')}`}
                                                {stop.id
                                                    ? ''
                                                    : ` · ${t('editors.quest.constructor.newBadge')}`}
                                            </Text>
                                        </Box>
                                        <ActionIcon
                                            size='sm'
                                            variant='subtle'
                                            color='red'
                                            style={{ flex: '0 0 auto' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeStop(stop.localKey);
                                            }}
                                        >
                                            <IconTrash size={14} />
                                        </ActionIcon>
                                    </Box>
                                );
                            })}
                            {!stops.length && (
                                <Text size='sm' c='dimmed'>
                                    {t('editors.quest.constructor.noStops')}
                                </Text>
                            )}

                            <Box
                                onClick={() => selectBookend('finale')}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '8px 10px',
                                    border:
                                        selectedBookend === 'finale'
                                            ? '1px solid var(--mantine-color-blue-5)'
                                            : '1px solid var(--mantine-color-gray-3)',
                                    borderRadius: 'var(--mantine-radius-sm)',
                                    background:
                                        selectedBookend === 'finale'
                                            ? 'var(--mantine-color-blue-0)'
                                            : 'var(--mantine-color-gray-0)',
                                    cursor: 'pointer',
                                }}
                            >
                                <Box style={{ flex: '1 1 auto', minWidth: 0 }}>
                                    <Text size='sm' fw={600} truncate>
                                        {t('editors.quest.constructor.bookendFinale')}
                                    </Text>
                                    <Text size='xs' c='dimmed' truncate>
                                        {t('editors.quest.constructor.bookendFixed')}
                                        {finaleFilled(arcLocales[contentLang])
                                            ? ''
                                            : ` · ${t('editors.quest.constructor.noText')}`}
                                    </Text>
                                </Box>
                            </Box>
                        </Stack>
                    </Section>
                </Box>

                <Box style={{ minWidth: 0, maxHeight: '70vh', overflow: 'auto' }}>
                    {selectedBookend === 'prologue' ? (
                        <Stack gap='lg' pr='xs'>
                            <LanguageChips
                                languages={contentLangChips}
                                value={contentLang}
                                onChange={(v) => setContentLang(v as Languages)}
                            />
                            <PrologueArcPanel
                                questId={questId}
                                language={contentLang}
                                draft={activeArcLocale}
                                onChange={patchArcLocale}
                            />
                        </Stack>
                    ) : selectedBookend === 'finale' ? (
                        <Stack gap='lg' pr='xs'>
                            <LanguageChips
                                languages={contentLangChips}
                                value={contentLang}
                                onChange={(v) => setContentLang(v as Languages)}
                            />
                            <FinaleArcPanel
                                questId={questId}
                                language={contentLang}
                                draft={activeArcLocale}
                                onChange={patchArcLocale}
                            />
                        </Stack>
                    ) : selectedAttraction && !selectedStop ? (
                        <Stack gap='sm' pr='xs'>
                            <Text fw={600} size='sm'>
                                {selectedAttraction.displayTitle || selectedAttraction.title}
                            </Text>
                            <FieldHint>
                                {selectedAttraction.address ||
                                    t('editors.quest.constructor.place.noAddress')}
                                {selectedAttraction.lat != null && selectedAttraction.lng != null
                                    ? ` · ${selectedAttraction.lat.toFixed(4)}, ${selectedAttraction.lng.toFixed(4)}`
                                    : ''}
                            </FieldHint>
                            <Button
                                size='xs'
                                onClick={() => addStopForAttraction(selectedAttraction)}
                            >
                                {t('editors.quest.constructor.addToQuest')}
                            </Button>
                        </Stack>
                    ) : selectedStop ? (
                        <Stack gap='lg' pr='xs'>
                            <Section
                                boxed
                                title={t('editors.quest.constructor.place.title')}
                                description={t('editors.quest.constructor.place.description')}
                            >
                                <Select
                                    label={t('editors.quest.constructor.place.attraction')}
                                    searchable
                                    data={attractionOptions}
                                    value={String(selectedStop.attractionId)}
                                    onChange={(v) =>
                                        v &&
                                        patchStop(selectedStop.localKey, {
                                            attractionId: Number(v),
                                        })
                                    }
                                />
                                {attraction && (
                                    <FieldHint>
                                        {attraction.address ||
                                            t('editors.quest.constructor.place.noAddress')}
                                        {attraction.lat != null && attraction.lng != null
                                            ? ` · ${attraction.lat.toFixed(4)}, ${attraction.lng.toFixed(4)}`
                                            : ''}
                                    </FieldHint>
                                )}
                                <NumberInput
                                    label={t('editors.quest.constructor.place.gpsRadius')}
                                    min={5}
                                    value={selectedStop.gpsRadiusM}
                                    onChange={(v) =>
                                        patchStop(selectedStop.localKey, {
                                            gpsRadiusM: typeof v === 'number' ? v : 40,
                                        })
                                    }
                                />
                                <Checkbox
                                    label={t('editors.quest.constructor.place.keyMilestone')}
                                    checked={selectedStop.isKeyMilestone}
                                    onChange={(e) =>
                                        patchStop(selectedStop.localKey, {
                                            isKeyMilestone: e.currentTarget.checked,
                                        })
                                    }
                                />
                            </Section>

                            <Section
                                boxed
                                title={t('editors.quest.constructor.stopPhoto.title')}
                                description={t('editors.quest.constructor.stopPhoto.description')}
                            >
                                <MediaDropzone
                                    label=''
                                    kind='image'
                                    imageType={ImageTypeEnum.CITY_READY_ROUTE}
                                    allowUrl={false}
                                    value={selectedStop.photoUrl}
                                    onChange={(url) =>
                                        patchStop(selectedStop.localKey, { photoUrl: url })
                                    }
                                />
                            </Section>

                            <Section
                                boxed
                                title={t('editors.quest.constructor.challengeMechanics.title')}
                                description={t(
                                    'editors.quest.constructor.challengeMechanics.description',
                                )}
                            >
                                <Select
                                    label={t('editors.quest.constructor.challengeMechanics.type')}
                                    data={challengeTypeOptions}
                                    value={selectedStop.challengeType}
                                    onChange={(v) =>
                                        v &&
                                        patchStop(selectedStop.localKey, {
                                            challengeType: v as CityQuestChallengeTypeEnum,
                                        })
                                    }
                                />
                                {selectedStop.challengeType ===
                                    CityQuestChallengeTypeEnum.CHOICE && (
                                    <TextInput
                                        label={t(
                                            'editors.quest.constructor.challengeMechanics.choiceIds',
                                        )}
                                        description={t(
                                            'editors.quest.constructor.challengeMechanics.choiceIdsHint',
                                        )}
                                        value={selectedStop.choiceIds.join(', ')}
                                        onChange={(e) =>
                                            patchStop(selectedStop.localKey, {
                                                choiceIds: e.currentTarget.value
                                                    .split(',')
                                                    .map((id) => id.trim())
                                                    .filter(Boolean),
                                            })
                                        }
                                    />
                                )}
                                {(selectedStop.challengeType ===
                                    CityQuestChallengeTypeEnum.SENSOR ||
                                    selectedStop.challengeType ===
                                        CityQuestChallengeTypeEnum.AR_HUNT) && (
                                    <FieldHint>
                                        {t(
                                            'editors.quest.constructor.challengeMechanics.doneAnswerNote',
                                        )}
                                    </FieldHint>
                                )}
                                {selectedStop.challengeType ===
                                    CityQuestChallengeTypeEnum.SENSOR && (
                                    <SensorConfigFields
                                        value={selectedStop.sensorConfig}
                                        onChange={(sensorConfig) =>
                                            patchStop(selectedStop.localKey, { sensorConfig })
                                        }
                                    />
                                )}
                                {selectedStop.challengeType ===
                                    CityQuestChallengeTypeEnum.AR_HUNT && (
                                    <ArHuntConfigFields
                                        value={selectedStop.arHunt}
                                        onChange={(arHunt) =>
                                            patchStop(selectedStop.localKey, { arHunt })
                                        }
                                        defaultCenter={
                                            attraction &&
                                            attraction.lat != null &&
                                            attraction.lng != null
                                                ? { lat: attraction.lat, lng: attraction.lng }
                                                : null
                                        }
                                    />
                                )}
                                {selectedStop.challengeType ===
                                    CityQuestChallengeTypeEnum.TIME_DYNAMIC && (
                                    <DynamicAnswerFields
                                        rule={selectedStop.dynamicAnswerRule}
                                        tolerance={selectedStop.dynamicAnswerTolerance}
                                        onChange={(patch) =>
                                            patchStop(selectedStop.localKey, patch)
                                        }
                                    />
                                )}
                                {selectedStop.challengeType ===
                                    CityQuestChallengeTypeEnum.PHOTO && (
                                    <NumberInput
                                        label={t(
                                            'editors.quest.constructor.challengeMechanics.photoCollageSlot',
                                        )}
                                        description={t(
                                            'editors.quest.constructor.challengeMechanics.photoCollageSlotHint',
                                        )}
                                        min={1}
                                        value={selectedStop.photoCollageSlot ?? ''}
                                        onChange={(v) =>
                                            patchStop(selectedStop.localKey, {
                                                photoCollageSlot: typeof v === 'number' ? v : null,
                                            })
                                        }
                                    />
                                )}
                                <NumberInput
                                    label={t(
                                        'editors.quest.constructor.challengeMechanics.expectedSolveTime',
                                    )}
                                    min={0}
                                    value={selectedStop.expectedSolveSeconds ?? ''}
                                    onChange={(v) =>
                                        patchStop(selectedStop.localKey, {
                                            expectedSolveSeconds: typeof v === 'number' ? v : null,
                                        })
                                    }
                                />
                                <TextInput
                                    label={t(
                                        'editors.quest.constructor.challengeMechanics.telemetryKey',
                                    )}
                                    value={selectedStop.telemetryEventKey || ''}
                                    onChange={(e) =>
                                        patchStop(selectedStop.localKey, {
                                            telemetryEventKey: e.currentTarget.value.trim() || null,
                                        })
                                    }
                                />
                            </Section>

                            <Section
                                boxed
                                title={t('editors.quest.constructor.hintPenalties.title')}
                                description={t(
                                    'editors.quest.constructor.hintPenalties.description',
                                )}
                            >
                                <Stack gap='xs'>
                                    {selectedStop.hintLevels.map((hint, index) => (
                                        <Group key={hint.level} gap='xs' wrap='nowrap'>
                                            <Badge variant='light'>
                                                {t(
                                                    'editors.quest.constructor.hintPenalties.level',
                                                    {
                                                        n: hint.level,
                                                    },
                                                )}
                                            </Badge>
                                            <NumberInput
                                                size='xs'
                                                min={0}
                                                label={t(
                                                    'editors.quest.constructor.hintPenalties.penalty',
                                                )}
                                                value={hint.penalty}
                                                onChange={(v) => {
                                                    const next = [...selectedStop.hintLevels];

                                                    next[index] = {
                                                        ...hint,
                                                        penalty: typeof v === 'number' ? v : 0,
                                                    };
                                                    patchStop(selectedStop.localKey, {
                                                        hintLevels: next,
                                                    });
                                                }}
                                            />
                                            <ActionIcon
                                                color='red'
                                                variant='subtle'
                                                onClick={() =>
                                                    patchStop(selectedStop.localKey, {
                                                        hintLevels: selectedStop.hintLevels.filter(
                                                            (h) => h.level !== hint.level,
                                                        ),
                                                    })
                                                }
                                            >
                                                <IconTrash size={14} />
                                            </ActionIcon>
                                        </Group>
                                    ))}
                                    <Button
                                        size='xs'
                                        variant='light'
                                        leftSection={<IconPlus size={14} />}
                                        onClick={() => {
                                            const levels = selectedStop.hintLevels;
                                            const level =
                                                (levels[levels.length - 1]?.level || 0) + 1;

                                            patchStop(selectedStop.localKey, {
                                                hintLevels: [
                                                    ...levels,
                                                    { level, penalty: 5 * level },
                                                ],
                                            });
                                        }}
                                    >
                                        {t('editors.quest.constructor.hintPenalties.addLevel')}
                                    </Button>
                                </Stack>
                            </Section>

                            <Section
                                boxed
                                title={t('editors.quest.constructor.stopTexts.title')}
                                description={t('editors.quest.constructor.stopTexts.description', {
                                    lang: contentLang.toUpperCase(),
                                })}
                            >
                                <LanguageChips
                                    languages={contentLangChips}
                                    value={contentLang}
                                    onChange={(v) => setContentLang(v as Languages)}
                                />
                                {!stopPlayable(selectedStopLocale) && (
                                    <FieldHint>
                                        {t('editors.quest.constructor.stopTexts.needsPromptHint', {
                                            lang: contentLang.toUpperCase(),
                                        })}
                                    </FieldHint>
                                )}
                                <TextInput
                                    label={t('editors.quest.constructor.stopTexts.chapterTitle')}
                                    value={selectedStopLocale.chapterTitle}
                                    onChange={(e) =>
                                        patchStopLocale(selectedStop.localKey, {
                                            chapterTitle: e.currentTarget.value,
                                        })
                                    }
                                />
                                <TextInput
                                    label={t(
                                        'editors.quest.constructor.stopTexts.nameBeforeSolving',
                                    )}
                                    value={selectedStopLocale.displayNameBeforeSolve}
                                    onChange={(e) =>
                                        patchStopLocale(selectedStop.localKey, {
                                            displayNameBeforeSolve: e.currentTarget.value,
                                        })
                                    }
                                />
                                <Textarea
                                    label={t('editors.quest.constructor.stopTexts.fromPrevious')}
                                    minRows={2}
                                    autosize
                                    value={selectedStopLocale.fromPrevious}
                                    onChange={(e) =>
                                        patchStopLocale(selectedStop.localKey, {
                                            fromPrevious: e.currentTarget.value,
                                        })
                                    }
                                />
                                <Textarea
                                    label={t('editors.quest.constructor.stopTexts.walkMicroTask')}
                                    minRows={2}
                                    autosize
                                    value={selectedStopLocale.walkMicroTask}
                                    onChange={(e) =>
                                        patchStopLocale(selectedStop.localKey, {
                                            walkMicroTask: e.currentTarget.value,
                                        })
                                    }
                                />
                                <Textarea
                                    label={t(
                                        'editors.quest.constructor.stopTexts.arrivalInstruction',
                                    )}
                                    minRows={2}
                                    autosize
                                    value={selectedStopLocale.arrivalInstruction}
                                    onChange={(e) =>
                                        patchStopLocale(selectedStop.localKey, {
                                            arrivalInstruction: e.currentTarget.value,
                                        })
                                    }
                                />
                                <Textarea
                                    label={t('editors.quest.constructor.stopTexts.storyBefore')}
                                    minRows={3}
                                    autosize
                                    value={selectedStopLocale.storyBefore}
                                    onChange={(e) =>
                                        patchStopLocale(selectedStop.localKey, {
                                            storyBefore: e.currentTarget.value,
                                        })
                                    }
                                />
                                <Textarea
                                    label={t(
                                        'editors.quest.constructor.stopTexts.challengePrompt',
                                        {
                                            type: enumLabel(
                                                t,
                                                'enums.challengeType',
                                                selectedStop.challengeType,
                                            ),
                                        },
                                    )}
                                    minRows={2}
                                    autosize
                                    value={selectedStopLocale.challengePrompt}
                                    onChange={(e) =>
                                        patchStopLocale(selectedStop.localKey, {
                                            challengePrompt: e.currentTarget.value,
                                        })
                                    }
                                />
                                <TextInput
                                    label={t('editors.quest.constructor.stopTexts.acceptedAnswers')}
                                    description={t(
                                        'editors.quest.constructor.stopTexts.acceptedAnswersHint',
                                    )}
                                    value={selectedStopLocale.acceptedAnswers}
                                    onChange={(e) =>
                                        patchStopLocale(selectedStop.localKey, {
                                            acceptedAnswers: e.currentTarget.value,
                                        })
                                    }
                                />
                                {selectedStop.challengeType ===
                                    CityQuestChallengeTypeEnum.CHOICE && (
                                    <Stack gap='xs'>
                                        <Text size='sm' fw={600}>
                                            {t('editors.quest.constructor.stopTexts.choiceLabels')}
                                        </Text>
                                        {(selectedStop.choiceIds || []).map((choiceId) => {
                                            const current = selectedStopLocale.choices.find(
                                                (c) => c.id === choiceId,
                                            );

                                            return (
                                                <TextInput
                                                    key={choiceId}
                                                    label={choiceId}
                                                    value={current?.label || ''}
                                                    onChange={(e) => {
                                                        const rest =
                                                            selectedStopLocale.choices.filter(
                                                                (c) => c.id !== choiceId,
                                                            );

                                                        patchStopLocale(selectedStop.localKey, {
                                                            choices: [
                                                                ...rest,
                                                                {
                                                                    id: choiceId,
                                                                    label: e.currentTarget.value,
                                                                },
                                                            ],
                                                        });
                                                    }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                )}
                                {selectedStop.challengeType ===
                                    CityQuestChallengeTypeEnum.SENSOR && (
                                    <SensorTextsFields
                                        value={selectedStopLocale.sensorTexts}
                                        onChange={(sensorTexts) =>
                                            patchStopLocale(selectedStop.localKey, { sensorTexts })
                                        }
                                    />
                                )}
                                {selectedStop.challengeType ===
                                    CityQuestChallengeTypeEnum.CIPHER && (
                                    <CipherConfigFields
                                        value={selectedStopLocale.cipher}
                                        onChange={(cipher) =>
                                            patchStopLocale(selectedStop.localKey, { cipher })
                                        }
                                    />
                                )}
                                {selectedStop.challengeType ===
                                    CityQuestChallengeTypeEnum.AR_HUNT && (
                                    <ArHuntTextsFields
                                        value={selectedStopLocale.arHuntTexts}
                                        onChange={(arHuntTexts) =>
                                            patchStopLocale(selectedStop.localKey, { arHuntTexts })
                                        }
                                        symbolIds={(selectedStop.arHunt?.symbols || []).map(
                                            (symbol) => symbol.id,
                                        )}
                                    />
                                )}
                                {selectedStop.challengeType ===
                                    CityQuestChallengeTypeEnum.PHOTO && (
                                    <>
                                        <Textarea
                                            label={t(
                                                'editors.quest.constructor.stopTexts.photoChallengePrompt',
                                            )}
                                            minRows={2}
                                            autosize
                                            value={selectedStopLocale.photoChallengePrompt}
                                            onChange={(e) =>
                                                patchStopLocale(selectedStop.localKey, {
                                                    photoChallengePrompt: e.currentTarget.value,
                                                })
                                            }
                                        />
                                        <Textarea
                                            label={t(
                                                'editors.quest.constructor.stopTexts.photoExpected',
                                            )}
                                            description={t(
                                                'editors.quest.constructor.stopTexts.photoExpectedHint',
                                            )}
                                            minRows={2}
                                            autosize
                                            value={
                                                selectedStopLocale.photoVerification?.expected || ''
                                            }
                                            onChange={(e) =>
                                                patchStopLocale(selectedStop.localKey, {
                                                    photoVerification: {
                                                        ...(selectedStopLocale.photoVerification ||
                                                            {}),
                                                        expected: e.currentTarget.value,
                                                    },
                                                })
                                            }
                                        />
                                        <NumberInput
                                            label={t(
                                                'editors.quest.constructor.stopTexts.photoMinConfidence',
                                            )}
                                            min={0}
                                            max={1}
                                            step={0.05}
                                            decimalScale={2}
                                            value={
                                                selectedStopLocale.photoVerification
                                                    ?.min_confidence ?? ''
                                            }
                                            onChange={(v) =>
                                                patchStopLocale(selectedStop.localKey, {
                                                    photoVerification: {
                                                        expected: '',
                                                        ...(selectedStopLocale.photoVerification ||
                                                            {}),
                                                        min_confidence:
                                                            typeof v === 'number' ? v : undefined,
                                                    },
                                                })
                                            }
                                        />
                                    </>
                                )}
                                {(selectedStop.hintLevels || []).map((level) => {
                                    const hint = selectedStopLocale.hints.find(
                                        (h) => h.level === level.level,
                                    );

                                    return (
                                        <Textarea
                                            key={level.level}
                                            label={t(
                                                'editors.quest.constructor.stopTexts.hintLevel',
                                                { level: level.level, penalty: level.penalty },
                                            )}
                                            minRows={2}
                                            autosize
                                            value={hint?.text || ''}
                                            onChange={(e) => {
                                                const rest = selectedStopLocale.hints.filter(
                                                    (h) => h.level !== level.level,
                                                );

                                                patchStopLocale(selectedStop.localKey, {
                                                    hints: [
                                                        ...rest,
                                                        {
                                                            level: level.level,
                                                            text: e.currentTarget.value,
                                                        },
                                                    ].sort((a, b) => a.level - b.level),
                                                });
                                            }}
                                        />
                                    );
                                })}
                                <Textarea
                                    label={t(
                                        'editors.quest.constructor.stopTexts.solutionExplanation',
                                    )}
                                    minRows={2}
                                    autosize
                                    value={selectedStopLocale.solutionExplanation}
                                    onChange={(e) =>
                                        patchStopLocale(selectedStop.localKey, {
                                            solutionExplanation: e.currentTarget.value,
                                        })
                                    }
                                />
                                <Textarea
                                    label={t('editors.quest.constructor.stopTexts.storyAfter')}
                                    minRows={3}
                                    autosize
                                    value={selectedStopLocale.storyAfter}
                                    onChange={(e) =>
                                        patchStopLocale(selectedStop.localKey, {
                                            storyAfter: e.currentTarget.value,
                                        })
                                    }
                                />
                                <Textarea
                                    label={t('editors.quest.constructor.stopTexts.historicalNote')}
                                    minRows={2}
                                    autosize
                                    value={selectedStopLocale.historicalNote}
                                    onChange={(e) =>
                                        patchStopLocale(selectedStop.localKey, {
                                            historicalNote: e.currentTarget.value,
                                        })
                                    }
                                />
                                <TextInput
                                    label={t('editors.quest.constructor.stopTexts.metaToken')}
                                    description={t(
                                        'editors.quest.constructor.stopTexts.metaTokenHint',
                                    )}
                                    value={selectedStopLocale.metaToken}
                                    onChange={(e) =>
                                        patchStopLocale(selectedStop.localKey, {
                                            metaToken: e.currentTarget.value,
                                        })
                                    }
                                />
                                {(['story', 'walk', 'challenge'] as const).map((part) => (
                                    <Group key={part} gap='xs' align='flex-end' wrap='nowrap'>
                                        <TextInput
                                            label={t(
                                                `editors.quest.constructor.stopTexts.${part}AudioUrl`,
                                            )}
                                            value={selectedStopLocale[`${part}AudioUrl`]}
                                            onChange={(e) =>
                                                patchStopLocale(selectedStop.localKey, {
                                                    [`${part}AudioUrl`]: e.currentTarget.value,
                                                } as Partial<StopLocaleDraft>)
                                            }
                                            style={{ flex: 1 }}
                                        />
                                        <Button
                                            size='xs'
                                            variant='light'
                                            loading={ttsPart === part}
                                            disabled={!selectedStop.id || ttsPart !== null}
                                            onClick={() => generateStopTts(part)}
                                            mb={2}
                                        >
                                            {t('editors.quest.constructor.stopTexts.generateTts')}
                                        </Button>
                                    </Group>
                                ))}
                                {!selectedStop.id && (
                                    <FieldHint>
                                        {t(
                                            'editors.quest.constructor.stopTexts.generateTtsSaveFirst',
                                        )}
                                    </FieldHint>
                                )}
                            </Section>

                            <Section
                                boxed
                                title={t('editors.quest.constructor.extras.title')}
                                description={t('editors.quest.constructor.extras.description', {
                                    lang: contentLang.toUpperCase(),
                                })}
                            >
                                <StopExtrasAccordion
                                    stop={selectedStop}
                                    locale={selectedStopLocale}
                                    onPatchStop={(patch) => patchStop(selectedStop.localKey, patch)}
                                    onPatchLocale={(patch) =>
                                        patchStopLocale(selectedStop.localKey, patch)
                                    }
                                />
                            </Section>
                        </Stack>
                    ) : (
                        <Text c='dimmed'>
                            {t('editors.quest.constructor.selectStopOrAttraction')}
                        </Text>
                    )}
                </Box>
            </Box>

            <Section
                boxed
                title={t('editors.quest.constructor.routeEvents.title')}
                description={t('editors.quest.constructor.routeEvents.description', {
                    lang: contentLang.toUpperCase(),
                })}
            >
                <LanguageChips
                    languages={contentLanguages.map((lang) => ({
                        value: lang,
                        label: lang.toUpperCase(),
                        mark:
                            lang === contentLang
                                ? dirty
                                    ? ('dirty' as const)
                                    : ('filled' as const)
                                : ('empty' as const),
                    }))}
                    value={contentLang}
                    onChange={(v) => setContentLang(v as Languages)}
                />
                <Stack gap='sm'>
                    {events.map((event) => {
                        const eventLocale = event.locales[contentLang] || emptyEventLocale();
                        const triggerIndex = stops.findIndex(
                            (stop) => stop.localKey === event.triggerAfterStopKey,
                        );
                        const chase = event.chase || {
                            durationSec: 0,
                            spawnDistanceM: 0,
                            catchRadiusM: 0,
                        };
                        const chaseTexts = eventLocale.chaseTexts || {};

                        return (
                            <Accordion key={event.localKey} variant='separated'>
                                <Accordion.Item value={event.localKey}>
                                    <Accordion.Control>
                                        <Group gap='xs'>
                                            <Text size='sm'>
                                                {event.key ||
                                                    t(
                                                        'editors.quest.constructor.routeEvents.untitledEvent',
                                                    )}
                                            </Text>
                                            <Badge size='xs' variant='light'>
                                                {enumLabel(t, 'enums.routeEventType', event.type)}
                                            </Badge>
                                            <Text size='xs' c='dimmed'>
                                                {triggerIndex >= 0
                                                    ? t(
                                                          'editors.quest.constructor.routeEvents.afterStopShort',
                                                          { n: triggerIndex + 1 },
                                                      )
                                                    : event.triggerAfterStopsCompleted != null
                                                      ? t(
                                                            'editors.quest.constructor.routeEvents.afterStopsCompletedShort',
                                                            {
                                                                n: event.triggerAfterStopsCompleted,
                                                            },
                                                        )
                                                      : t(
                                                            'editors.quest.constructor.routeEvents.noTrigger',
                                                        )}
                                            </Text>
                                        </Group>
                                    </Accordion.Control>
                                    <Accordion.Panel>
                                        <Stack gap='sm'>
                                            <Group gap='xs' align='flex-end' wrap='wrap'>
                                                <TextInput
                                                    label={t(
                                                        'editors.quest.constructor.routeEvents.key',
                                                    )}
                                                    size='xs'
                                                    value={event.key}
                                                    onChange={(e) =>
                                                        patchEvent(event.localKey, {
                                                            key: e.currentTarget.value,
                                                        })
                                                    }
                                                    style={{ width: 150 }}
                                                />
                                                <Select
                                                    label={t(
                                                        'editors.quest.constructor.routeEvents.type',
                                                    )}
                                                    size='xs'
                                                    data={eventTypeOptions}
                                                    value={event.type}
                                                    onChange={(v) =>
                                                        v &&
                                                        patchEvent(event.localKey, {
                                                            type: v as CityQuestRouteEventTypeEnum,
                                                        })
                                                    }
                                                    style={{ width: 170 }}
                                                />
                                                <Select
                                                    label={t(
                                                        'editors.quest.constructor.routeEvents.afterStop',
                                                    )}
                                                    size='xs'
                                                    clearable
                                                    data={stops.map((stop, index) => ({
                                                        value: stop.localKey,
                                                        label: `${index + 1}. ${stopTitle(stop)}`,
                                                    }))}
                                                    value={event.triggerAfterStopKey}
                                                    onChange={(v) =>
                                                        patchEvent(event.localKey, {
                                                            triggerAfterStopKey: v,
                                                        })
                                                    }
                                                    style={{ width: 220 }}
                                                />
                                                <NumberInput
                                                    label={t(
                                                        'editors.quest.constructor.routeEvents.afterStopsCompleted',
                                                    )}
                                                    description={t(
                                                        'editors.quest.constructor.routeEvents.afterStopsCompletedHint',
                                                    )}
                                                    size='xs'
                                                    min={1}
                                                    value={event.triggerAfterStopsCompleted ?? ''}
                                                    onChange={(v) =>
                                                        patchEvent(event.localKey, {
                                                            triggerAfterStopsCompleted:
                                                                typeof v === 'number' ? v : null,
                                                        })
                                                    }
                                                    style={{ width: 160 }}
                                                />
                                                <NumberInput
                                                    label={t(
                                                        'editors.quest.constructor.routeEvents.bonus',
                                                    )}
                                                    size='xs'
                                                    value={event.bonusPoints ?? ''}
                                                    onChange={(v) =>
                                                        patchEvent(event.localKey, {
                                                            bonusPoints:
                                                                typeof v === 'number' ? v : null,
                                                        })
                                                    }
                                                    style={{ width: 100 }}
                                                />
                                                <NumberInput
                                                    label={t(
                                                        'editors.quest.constructor.routeEvents.timer',
                                                    )}
                                                    size='xs'
                                                    value={event.softTimerSeconds ?? ''}
                                                    onChange={(v) =>
                                                        patchEvent(event.localKey, {
                                                            softTimerSeconds:
                                                                typeof v === 'number' ? v : null,
                                                        })
                                                    }
                                                    style={{ width: 110 }}
                                                />
                                                <Select
                                                    label={t(
                                                        'editors.quest.constructor.routeEvents.challenge',
                                                    )}
                                                    size='xs'
                                                    clearable
                                                    data={challengeTypeOptions}
                                                    value={event.challengeType}
                                                    onChange={(v) =>
                                                        patchEvent(event.localKey, {
                                                            challengeType:
                                                                (v as CityQuestChallengeTypeEnum) ||
                                                                null,
                                                        })
                                                    }
                                                    style={{ width: 150 }}
                                                />
                                                <ActionIcon
                                                    color='red'
                                                    variant='subtle'
                                                    onClick={() =>
                                                        setEvents((list) =>
                                                            list.filter(
                                                                (e) =>
                                                                    e.localKey !== event.localKey,
                                                            ),
                                                        )
                                                    }
                                                >
                                                    <IconTrash size={16} />
                                                </ActionIcon>
                                            </Group>
                                            <TextInput
                                                label={t(
                                                    'editors.quest.constructor.routeEvents.from',
                                                )}
                                                value={eventLocale.fromName}
                                                onChange={(e) =>
                                                    patchEventLocale(event.localKey, {
                                                        fromName: e.currentTarget.value,
                                                    })
                                                }
                                            />
                                            <Textarea
                                                label={t(
                                                    'editors.quest.constructor.routeEvents.text',
                                                )}
                                                minRows={3}
                                                autosize
                                                value={eventLocale.text}
                                                onChange={(e) =>
                                                    patchEventLocale(event.localKey, {
                                                        text: e.currentTarget.value,
                                                    })
                                                }
                                            />
                                            {event.type ===
                                                CityQuestRouteEventTypeEnum.GPS_CHASE && (
                                                <>
                                                    <Group gap='xs' wrap='wrap'>
                                                        <NumberInput
                                                            label={t(
                                                                'editors.quest.constructor.routeEvents.chase.duration',
                                                            )}
                                                            size='xs'
                                                            min={1}
                                                            value={event.chase?.durationSec ?? ''}
                                                            onChange={(v) =>
                                                                patchEvent(event.localKey, {
                                                                    chase: {
                                                                        ...chase,
                                                                        durationSec:
                                                                            typeof v === 'number'
                                                                                ? v
                                                                                : 0,
                                                                    },
                                                                })
                                                            }
                                                            style={{ width: 150 }}
                                                        />
                                                        <NumberInput
                                                            label={t(
                                                                'editors.quest.constructor.routeEvents.chase.spawnDistance',
                                                            )}
                                                            size='xs'
                                                            min={1}
                                                            value={
                                                                event.chase?.spawnDistanceM ?? ''
                                                            }
                                                            onChange={(v) =>
                                                                patchEvent(event.localKey, {
                                                                    chase: {
                                                                        ...chase,
                                                                        spawnDistanceM:
                                                                            typeof v === 'number'
                                                                                ? v
                                                                                : 0,
                                                                    },
                                                                })
                                                            }
                                                            style={{ width: 150 }}
                                                        />
                                                        <NumberInput
                                                            label={t(
                                                                'editors.quest.constructor.routeEvents.chase.catchRadius',
                                                            )}
                                                            size='xs'
                                                            min={1}
                                                            value={event.chase?.catchRadiusM ?? ''}
                                                            onChange={(v) =>
                                                                patchEvent(event.localKey, {
                                                                    chase: {
                                                                        ...chase,
                                                                        catchRadiusM:
                                                                            typeof v === 'number'
                                                                                ? v
                                                                                : 0,
                                                                    },
                                                                })
                                                            }
                                                            style={{ width: 150 }}
                                                        />
                                                        <NumberInput
                                                            label={t(
                                                                'editors.quest.constructor.routeEvents.chase.targetSpeed',
                                                            )}
                                                            size='xs'
                                                            min={0}
                                                            step={0.1}
                                                            decimalScale={1}
                                                            value={
                                                                event.chase?.targetSpeedMps ?? ''
                                                            }
                                                            onChange={(v) =>
                                                                patchEvent(event.localKey, {
                                                                    chase: {
                                                                        ...chase,
                                                                        targetSpeedMps:
                                                                            typeof v === 'number'
                                                                                ? v
                                                                                : undefined,
                                                                    },
                                                                })
                                                            }
                                                            style={{ width: 150 }}
                                                        />
                                                    </Group>
                                                    <Group grow>
                                                        <TextInput
                                                            label={t(
                                                                'editors.quest.constructor.routeEvents.chase.targetLabel',
                                                            )}
                                                            value={chaseTexts.targetLabel || ''}
                                                            onChange={(e) =>
                                                                patchEventLocale(event.localKey, {
                                                                    chaseTexts: {
                                                                        ...chaseTexts,
                                                                        targetLabel:
                                                                            e.currentTarget.value,
                                                                    },
                                                                })
                                                            }
                                                        />
                                                        <TextInput
                                                            label={t(
                                                                'editors.quest.constructor.routeEvents.chase.successText',
                                                            )}
                                                            value={chaseTexts.successText || ''}
                                                            onChange={(e) =>
                                                                patchEventLocale(event.localKey, {
                                                                    chaseTexts: {
                                                                        ...chaseTexts,
                                                                        successText:
                                                                            e.currentTarget.value,
                                                                    },
                                                                })
                                                            }
                                                        />
                                                        <TextInput
                                                            label={t(
                                                                'editors.quest.constructor.routeEvents.chase.failText',
                                                            )}
                                                            value={chaseTexts.failText || ''}
                                                            onChange={(e) =>
                                                                patchEventLocale(event.localKey, {
                                                                    chaseTexts: {
                                                                        ...chaseTexts,
                                                                        failText:
                                                                            e.currentTarget.value,
                                                                    },
                                                                })
                                                            }
                                                        />
                                                    </Group>
                                                </>
                                            )}
                                            {!!event.challengeType && (
                                                <>
                                                    <Textarea
                                                        label={t(
                                                            'editors.quest.constructor.routeEvents.challengePrompt',
                                                            {
                                                                type: enumLabel(
                                                                    t,
                                                                    'enums.challengeType',
                                                                    event.challengeType,
                                                                ),
                                                            },
                                                        )}
                                                        minRows={2}
                                                        autosize
                                                        value={eventLocale.challengePrompt}
                                                        onChange={(e) =>
                                                            patchEventLocale(event.localKey, {
                                                                challengePrompt:
                                                                    e.currentTarget.value,
                                                            })
                                                        }
                                                    />
                                                    <TextInput
                                                        label={t(
                                                            'editors.quest.constructor.routeEvents.acceptedAnswers',
                                                        )}
                                                        value={eventLocale.acceptedAnswers}
                                                        onChange={(e) =>
                                                            patchEventLocale(event.localKey, {
                                                                acceptedAnswers:
                                                                    e.currentTarget.value,
                                                            })
                                                        }
                                                    />
                                                    {event.hintLevels.map((level, index) => {
                                                        const hint = eventLocale.hints.find(
                                                            (h) => h.level === level.level,
                                                        );

                                                        return (
                                                            <Group
                                                                key={level.level}
                                                                gap='xs'
                                                                align='flex-end'
                                                                wrap='nowrap'
                                                            >
                                                                <NumberInput
                                                                    size='xs'
                                                                    min={0}
                                                                    label={t(
                                                                        'editors.quest.constructor.hintPenalties.penalty',
                                                                    )}
                                                                    value={level.penalty}
                                                                    onChange={(v) => {
                                                                        const next = [
                                                                            ...event.hintLevels,
                                                                        ];

                                                                        next[index] = {
                                                                            ...level,
                                                                            penalty:
                                                                                typeof v ===
                                                                                'number'
                                                                                    ? v
                                                                                    : 0,
                                                                        };
                                                                        patchEvent(event.localKey, {
                                                                            hintLevels: next,
                                                                        });
                                                                    }}
                                                                    style={{ width: 110 }}
                                                                />
                                                                <Textarea
                                                                    size='xs'
                                                                    label={t(
                                                                        'editors.quest.constructor.stopTexts.hintLevel',
                                                                        {
                                                                            level: level.level,
                                                                            penalty: level.penalty,
                                                                        },
                                                                    )}
                                                                    minRows={1}
                                                                    autosize
                                                                    value={hint?.text || ''}
                                                                    onChange={(e) => {
                                                                        const rest =
                                                                            eventLocale.hints.filter(
                                                                                (h) =>
                                                                                    h.level !==
                                                                                    level.level,
                                                                            );

                                                                        patchEventLocale(
                                                                            event.localKey,
                                                                            {
                                                                                hints: [
                                                                                    ...rest,
                                                                                    {
                                                                                        level: level.level,
                                                                                        text: e
                                                                                            .currentTarget
                                                                                            .value,
                                                                                    },
                                                                                ].sort(
                                                                                    (a, b) =>
                                                                                        a.level -
                                                                                        b.level,
                                                                                ),
                                                                            },
                                                                        );
                                                                    }}
                                                                    style={{ flex: 1 }}
                                                                />
                                                                <ActionIcon
                                                                    color='red'
                                                                    variant='subtle'
                                                                    onClick={() =>
                                                                        patchEvent(event.localKey, {
                                                                            hintLevels:
                                                                                event.hintLevels.filter(
                                                                                    (h) =>
                                                                                        h.level !==
                                                                                        level.level,
                                                                                ),
                                                                        })
                                                                    }
                                                                >
                                                                    <IconTrash size={14} />
                                                                </ActionIcon>
                                                            </Group>
                                                        );
                                                    })}
                                                    <Button
                                                        size='xs'
                                                        variant='light'
                                                        leftSection={<IconPlus size={14} />}
                                                        onClick={() => {
                                                            const levels = event.hintLevels;
                                                            const level =
                                                                (levels[levels.length - 1]?.level ||
                                                                    0) + 1;

                                                            patchEvent(event.localKey, {
                                                                hintLevels: [
                                                                    ...levels,
                                                                    {
                                                                        level,
                                                                        penalty: 5 * level,
                                                                    },
                                                                ],
                                                            });
                                                        }}
                                                    >
                                                        {t(
                                                            'editors.quest.constructor.hintPenalties.addLevel',
                                                        )}
                                                    </Button>
                                                    <Textarea
                                                        label={t(
                                                            'editors.quest.constructor.routeEvents.solutionExplanation',
                                                        )}
                                                        minRows={2}
                                                        autosize
                                                        value={eventLocale.solutionExplanation}
                                                        onChange={(e) =>
                                                            patchEventLocale(event.localKey, {
                                                                solutionExplanation:
                                                                    e.currentTarget.value,
                                                            })
                                                        }
                                                    />
                                                </>
                                            )}
                                            <TextInput
                                                label={t(
                                                    'editors.quest.constructor.routeEvents.audioUrl',
                                                )}
                                                value={eventLocale.audioUrl}
                                                onChange={(e) =>
                                                    patchEventLocale(event.localKey, {
                                                        audioUrl: e.currentTarget.value,
                                                    })
                                                }
                                            />
                                        </Stack>
                                    </Accordion.Panel>
                                </Accordion.Item>
                            </Accordion>
                        );
                    })}
                    <Button
                        size='xs'
                        variant='light'
                        leftSection={<IconPlus size={14} />}
                        onClick={addEvent}
                    >
                        {t('editors.quest.constructor.routeEvents.addEvent')}
                    </Button>
                </Stack>
            </Section>

            <EditorFooter
                onCancel={onClose}
                onSave={save}
                saving={saving}
                saveLabel={t('editors.quest.constructor.saveStops')}
            />
        </Stack>
    );
};
